import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";

const SENTRY_DSN_CACHE_KEY = 'socrates-sentry-dsn';
const SENTRY_DSN_CACHE_EXPIRY_KEY = 'socrates-sentry-dsn-expiry';
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Get cached Sentry DSN from localStorage
 */
function getCachedDsn(): string | null {
  const cached = localStorage.getItem(SENTRY_DSN_CACHE_KEY);
  const expiry = localStorage.getItem(SENTRY_DSN_CACHE_EXPIRY_KEY);
  
  if (!cached || !expiry) {
    return null;
  }
  
  // Check if cache is expired
  const expiryTime = parseInt(expiry, 10);
  if (Date.now() > expiryTime) {
    // Cache expired, clear it
    localStorage.removeItem(SENTRY_DSN_CACHE_KEY);
    localStorage.removeItem(SENTRY_DSN_CACHE_EXPIRY_KEY);
    return null;
  }
  
  return cached;
}

/**
 * Cache Sentry DSN in localStorage
 */
function setCachedDsn(dsn: string): void {
  const expiryTime = Date.now() + CACHE_DURATION_MS;
  localStorage.setItem(SENTRY_DSN_CACHE_KEY, dsn);
  localStorage.setItem(SENTRY_DSN_CACHE_EXPIRY_KEY, expiryTime.toString());
}

/**
 * Initialize Sentry with DSN from Supabase secrets (via Edge Function)
 * Falls back to VITE_SENTRY_DSN env var for local development
 * Caches DSN in localStorage to avoid fetching on every page load
 */
export async function initSentry() {
  let dsn = import.meta.env.VITE_SENTRY_DSN;

  // If no env var, check cache first, then fetch from Supabase Edge Function
  if (!dsn) {
    // Try cache first
    dsn = getCachedDsn();
    
    // If no cache, fetch from Edge Function
    if (!dsn) {
      try {
        const { data, error } = await supabase.functions.invoke('get-app-config');
        if (!error && data?.sentryDsn) {
          dsn = data.sentryDsn;
          // Cache it for future use
          setCachedDsn(dsn);
        }
      } catch (error) {
        console.warn("Failed to fetch Sentry DSN from config:", error);
        // If fetch fails, try to use cached value (even if expired)
        const expiredCache = localStorage.getItem(SENTRY_DSN_CACHE_KEY);
        if (expiredCache) {
          console.warn("Using expired cached Sentry DSN");
          dsn = expiredCache;
        }
      }
    }
  }

  if (!dsn) {
    console.warn("Sentry DSN not configured. Error tracking disabled.");
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    
    // Performance Monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% in prod, 100% in dev
    
    // Session Replay (optional - captures user sessions for debugging)
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    
    // Only send errors in production by default
    enabled: import.meta.env.PROD || import.meta.env.VITE_SENTRY_FORCE_ENABLED === "true",
    
    // Filter out known non-critical errors
    beforeSend(event, hint) {
      const error = hint.originalException;
      
      // Ignore network errors that are common and expected
      if (error instanceof Error) {
        // Ignore cancelled requests
        if (error.name === "AbortError") return null;
        
        // Ignore ResizeObserver errors (browser quirk)
        if (error.message?.includes("ResizeObserver")) return null;
      }
      
      return event;
    },
    
    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration(),
      // Session replay integration
      Sentry.replayIntegration(),
    ],
  });
}

// Re-export Sentry for use throughout the app
export { Sentry };

